// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which sources may notify through Konvo, and through which channels. Stored server-side
// (the worker sends WhatsApp, mail, ClickUp and Agents pushes and honours the same switches); this
// store mirrors it for the in-app popups, desktop notifications, sounds and the settings page.
//
// Each source has a master switch (the phone list) and per-channel flags (the desktop table):
// phone push, this computer, pop-ups inside Konvo and sound. Only channels switched off are kept.
// Agents has no master switch. Not every source can use every channel (CHANNELS_FOR).

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

export type KonvoNotificationSource = 'konvo_chats' | 'teams' | 'whatsapp' | 'mail' | 'clickup';
export type NotificationChannelSource = KonvoNotificationSource | 'agents';
export type NotificationChannel = 'push' | 'desktop' | 'popup' | 'sound';
export type NotificationChannelPrefs = Partial<
	Record<NotificationChannelSource, Partial<Record<NotificationChannel, boolean>>>
>;

export const NOTIFICATION_CHANNELS: ReadonlyArray<NotificationChannel> = ['push', 'desktop', 'popup', 'sound'];

/**
 * The channels each source really uses. Mail has no pop-up; ClickUp only pushes; an Agents notice
 * that arrives as a Konvo message follows the Konvo chats row for everything but its push.
 */
export const CHANNELS_FOR: Record<NotificationChannelSource, ReadonlyArray<NotificationChannel>> = {
	konvo_chats: NOTIFICATION_CHANNELS,
	teams: NOTIFICATION_CHANNELS,
	whatsapp: NOTIFICATION_CHANNELS,
	mail: ['push', 'desktop', 'sound'],
	clickup: ['push'],
	agents: ['push'],
};

export interface KonvoNotificationPrefsPayload {
	konvo_chats?: boolean;
	teams?: boolean;
	whatsapp?: boolean;
	mail?: boolean;
	clickup?: boolean;
	channels?: NotificationChannelPrefs;
}

const MASTER_KEYS: ReadonlyArray<KonvoNotificationSource> = ['konvo_chats', 'teams', 'whatsapp', 'mail', 'clickup'];

/** The same merge as the server: master switches, then channel flags one by one (true forgets the flag). */
export function mergeNotificationPrefs(
	base: KonvoNotificationPrefsPayload,
	patch: KonvoNotificationPrefsPayload,
): KonvoNotificationPrefsPayload {
	const next: KonvoNotificationPrefsPayload = {...base, channels: {...base.channels}};
	for (const key of MASTER_KEYS) {
		if (typeof patch[key] === 'boolean') next[key] = patch[key];
	}
	for (const [source, flags] of Object.entries(patch.channels ?? {}) as Array<
		[NotificationChannelSource, Partial<Record<NotificationChannel, boolean>>]
	>) {
		const merged = {...next.channels?.[source]};
		for (const channel of NOTIFICATION_CHANNELS) {
			if (flags[channel] === true) delete merged[channel];
			else if (flags[channel] === false) merged[channel] = false;
		}
		if (Object.keys(merged).length > 0) next.channels![source] = merged;
		else delete next.channels![source];
	}
	return next;
}

class KonvoNotificationPrefsStore {
	konvoChats = true;
	teams = true;
	whatsapp = true;
	mail = true;
	clickup = true;
	channels: NotificationChannelPrefs = {};
	loaded = false;
	private loading: Promise<void> | null = null;

	constructor() {
		makeAutoObservable<this, 'loading'>(this, {loading: false}, {autoBind: true});
	}

	allows(source: KonvoNotificationSource): boolean {
		switch (source) {
			case 'konvo_chats':
				return this.konvoChats;
			case 'teams':
				return this.teams;
			case 'whatsapp':
				return this.whatsapp;
			case 'mail':
				return this.mail;
			case 'clickup':
				return this.clickup;
		}
	}

	/** The master switch (Agents has none) and the channel flag. */
	allowsChannel(source: NotificationChannelSource, channel: NotificationChannel): boolean {
		if (source !== 'agents' && !this.allows(source)) return false;
		return this.channels[source]?.[channel] !== false;
	}

	/** Fetch once; defaults (everything on) stand until the server answers. */
	ensureLoaded(): Promise<void> {
		if (this.loaded) return Promise.resolve();
		if (!this.loading) {
			this.loading = this.load().finally(() => {
				this.loading = null;
			});
		}
		return this.loading;
	}

	async load(): Promise<void> {
		try {
			const response = await http.get<KonvoNotificationPrefsPayload>(Endpoints.KONVO_NOTIFICATION_PREFS);
			this.applyRemote(response.body ?? {});
			runInAction(() => {
				this.loaded = true;
			});
		} catch {
			// defaults stand
		}
	}

	/** A KONVO_NOTIFICATION_PREFS_UPDATE from another window or device. */
	applyRemote(prefs: KonvoNotificationPrefsPayload): void {
		runInAction(() => {
			this.konvoChats = prefs.konvo_chats !== false;
			this.teams = prefs.teams !== false;
			this.whatsapp = prefs.whatsapp !== false;
			this.mail = prefs.mail !== false;
			this.clickup = prefs.clickup !== false;
			this.channels = prefs.channels && typeof prefs.channels === 'object' ? prefs.channels : {};
		});
	}

	private snapshot(): KonvoNotificationPrefsPayload {
		return {
			konvo_chats: this.konvoChats,
			teams: this.teams,
			whatsapp: this.whatsapp,
			mail: this.mail,
			clickup: this.clickup,
			channels: this.channels,
		};
	}

	/**
	 * The phone's switch for a source. Switching a source on whose channels are all off turns them
	 * all back on, so it never comes back silent.
	 */
	setSource(source: KonvoNotificationSource, value: boolean): Promise<void> {
		const allOff = CHANNELS_FOR[source].every((channel) => this.channels[source]?.[channel] === false);
		return this.save(value && allOff ? {[source]: true, channels: {[source]: allOn(source)}} : {[source]: value});
	}

	/**
	 * One cell of the desktop table. A cell of a switched-off source switches the source on with
	 * only that channel; clearing a source's last channel switches the source off (channels reset).
	 */
	setChannel(source: NotificationChannelSource, channel: NotificationChannel, value: boolean): Promise<void> {
		if (source === 'agents') return this.save({channels: {agents: {[channel]: value}}});
		if (value && !this.allows(source)) {
			const only = Object.fromEntries(CHANNELS_FOR[source].map((each) => [each, each === channel]));
			return this.save({[source]: true, channels: {[source]: only}});
		}
		const othersOn = CHANNELS_FOR[source].some((each) => each !== channel && this.allowsChannel(source, each));
		if (!value && !othersOn) return this.save({[source]: false, channels: {[source]: allOn(source)}});
		return this.save({channels: {[source]: {[channel]: value}}});
	}

	async save(patch: KonvoNotificationPrefsPayload): Promise<void> {
		const previous = this.snapshot();
		this.applyRemote(mergeNotificationPrefs(previous, patch));
		try {
			const response = await http.put<KonvoNotificationPrefsPayload>(Endpoints.KONVO_NOTIFICATION_PREFS, {
				body: patch,
			});
			this.applyRemote(response.body ?? mergeNotificationPrefs(previous, patch));
		} catch {
			this.applyRemote(previous);
		}
	}
}

function allOn(source: NotificationChannelSource): Partial<Record<NotificationChannel, boolean>> {
	return Object.fromEntries(CHANNELS_FOR[source].map((channel) => [channel, true]));
}

export default new KonvoNotificationPrefsStore();
